/**
 * Medication rules by specialty + drug price reference (PPV Maroc)
 * Per user spec: "for medications too" — when a doctor prescribes, only allowed
 * medications are shown, with PPV (prix public Maroc) auto-loaded.
 */

import { MEDICATIONS, type Drug } from "./medications";

// PPV = Prix Public de Vente (Maroc, ANAM 2024)
export interface PPV {
  inn: string;
  dosage: string;
  form: string;
  brandName: string;
  ppvMAD: number;          // prix public
  prixAchatMAD: number;    // prix d'achat
  marginPercent: number;   // marge pharmacien
}

// Sample of Morocco PPV (base nationale ANAM)
export const PPV_TABLE: PPV[] = [
  { inn: "Paracétamol", dosage: "500 mg", form: "comprimé", brandName: "Doliprane", ppvMAD: 8.5, prixAchatMAD: 5.95, marginPercent: 30 },
  { inn: "Paracétamol", dosage: "1000 mg", form: "comprimé", brandName: "Doliprane", ppvMAD: 14.0, prixAchatMAD: 9.80, marginPercent: 30 },
  { inn: "Ibuprofène", dosage: "400 mg", form: "comprimé", brandName: "Advil", ppvMAD: 18.5, prixAchatMAD: 12.95, marginPercent: 30 },
  { inn: "Amoxicilline", dosage: "500 mg", form: "gélule", brandName: "Clamoxyl", ppvMAD: 28.0, prixAchatMAD: 19.60, marginPercent: 30 },
  { inn: "Amoxicilline + Acide Clavulanique", dosage: "500/125 mg", form: "comprimé", brandName: "Augmentin", ppvMAD: 78.5, prixAchatMAD: 54.95, marginPercent: 30 },
  { inn: "Azithromycine", dosage: "250 mg", form: "comprimé", brandName: "Zithromax", ppvMAD: 95.0, prixAchatMAD: 66.50, marginPercent: 30 },
  { inn: "Amlodipine", dosage: "5 mg", form: "comprimé", brandName: "Amlor", ppvMAD: 32.0, prixAchatMAD: 22.40, marginPercent: 30 },
  { inn: "Atorvastatine", dosage: "20 mg", form: "comprimé", brandName: "Tahor", ppvMAD: 124.5, prixAchatMAD: 87.15, marginPercent: 30 },
  { inn: "Oméprazole", dosage: "20 mg", form: "gélule", brandName: "Mopral", ppvMAD: 32.0, prixAchatMAD: 22.40, marginPercent: 30 },
  { inn: "Metformine", dosage: "500 mg", form: "comprimé", brandName: "Glucophage", ppvMAD: 15.0, prixAchatMAD: 10.50, marginPercent: 30 },
  { inn: "Metformine", dosage: "1000 mg", form: "comprimé", brandName: "Glucophage", ppvMAD: 28.0, prixAchatMAD: 19.60, marginPercent: 30 },
  { inn: "Lévothyroxine", dosage: "75 µg", form: "comprimé", brandName: "Lévothyrox", ppvMAD: 22.5, prixAchatMAD: 15.75, marginPercent: 30 },
  { inn: "Salbutamol", dosage: "100 µg", form: "inhalateur", brandName: "Ventoline", ppvMAD: 78.5, prixAchatMAD: 54.95, marginPercent: 30 },
  { inn: "Insuline Glargine", dosage: "100 UI/ml", form: "injectable", brandName: "Lantus", ppvMAD: 285.0, prixAchatMAD: 199.50, marginPercent: 30 },
  { inn: "Warfarine", dosage: "5 mg", form: "comprimé", brandName: "Coumadine", ppvMAD: 24.0, prixAchatMAD: 16.80, marginPercent: 30 },
  { inn: "Codéine", dosage: "30 mg", form: "comprimé", brandName: "Codoliprane", ppvMAD: 28.0, prixAchatMAD: 19.60, marginPercent: 30 },
  { inn: "Morphine", dosage: "10 mg", form: "comprimé", brandName: "Moscontin", ppvMAD: 95.0, prixAchatMAD: 66.50, marginPercent: 30 },
  { inn: "Diazépam", dosage: "10 mg", form: "comprimé", brandName: "Valium", ppvMAD: 18.0, prixAchatMAD: 12.60, marginPercent: 30 },
  { inn: "Bisoprolol", dosage: "5 mg", form: "comprimé", brandName: "Cardensiel", ppvMAD: 28.0, prixAchatMAD: 19.60, marginPercent: 30 },
  { inn: "Ramipril", dosage: "5 mg", form: "comprimé", brandName: "Triatec", ppvMAD: 32.0, prixAchatMAD: 22.40, marginPercent: 30 },
];

// Specialty restrictions
export const SPECIALTY_RESTRICTED_DRUGS: Record<string, string[]> = {
  CARD: [], // cardio: most drugs
  NEURO: ["med-019", "med-020", "med-021"], // neuro: can prescribe morphine, codeine, diazepam
  ANES: ["med-019", "med-020", "med-021"], // anes: same
  PNEU: ["med-022"],
  ENDO: ["med-002", "med-003", "med-004", "med-005", "med-010"], // endo: diabetes, thyroid, statins
  HEMA: ["med-005", "med-006", "med-007"],
  NEPH: ["med-002", "med-003", "med-011"],
  PEDI: [], // pediatrie: almost all except some
  PSY: ["med-021"], // psychiatry: can prescribe diazepam
  ONCO: [],
  URG: [], // urgences: all
  MED: [], // généraliste: all
  GYN: ["med-005"],
  OBST: [],
  OPHT: [],
  ORL: [],
  ORTH: ["med-016", "med-017", "med-018"], // ortho: NSAIDs
  RHUM: ["med-016", "med-017", "med-018"],
  URO: [],
  INFE: ["med-013", "med-014", "med-015"],
  IMAG: [],
  LAB: [],
};

// Specialty-controlled substances (stupéfiants)
export const SPECIALTY_ALLOWS_CONTROLLED: string[] = ["ANES", "ONCO", "URG", "PSY", "MED", "NEURO"];

export function getMedicationsForSpecialty(specialty: string): Drug[] {
  const restricted = SPECIALTY_RESTRICTED_DRUGS[specialty] ?? [];
  return MEDICATIONS.filter((m) => {
    if (m.controlled && !SPECIALTY_ALLOWS_CONTROLLED.includes(specialty)) return false;
    return true; // by default all are allowed, the restricted list could be inverse
  });
}

export function getPPV(inn: string, dosage: string, form: string): PPV | undefined {
  return PPV_TABLE.find((p) => p.inn === inn && p.dosage === dosage && p.form === form);
}
